<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import {
  UiText,
  UiButton,
  UiIcon,
  UiIconButton,
  UiChip,
  UiDialog,
  UiInfoBox,
  UiSegmentedControl,
  UiTooltip,
} from '@shopexperts/shared';
import {
  useExpertSubscription,
  useExpertInvoices,
  type ExpertSubscriptionWithDisplay,
} from '@/app/composables/expert-manager/useExpertSubscription';
import InvoiceForm from './InvoiceForm.vue';
import ExpertPaymentInvitations from './ExpertPaymentInvitations.vue';
import {
  adminSubscriptionManagementOptions,
  canCancelLocally,
  canMutateInvoiceLocally,
  describeRecurrence,
  getLinkedInvitationId,
  getProviderStatusPresentation,
  getSubscriptionAccentTone,
  isManagedInvoice,
  isManagedSubscription,
  type AdminExpertInvoiceRecord,
  type AdminSubscriptionManagement,
} from '@/app/composables/expert-manager/adminManagedSubscription';
import type {
  AdminExpertDetail,
  RegisterExpertSubscriptionInvoiceRequest,
  UpdateExpertSubscriptionInvoiceRequest,
} from '@shopexperts/legacy-api';
import { sanitizeHttpUrl } from '@/app/utils/httpUrl';

const DEFAULT_CURRENCY = 'USD';

type InvoiceFormSubmitData = {
  v2_plan_id: number;
  invoice_type: 'external';
  amount: string;
  currency: string;
  issued_at: string;
  paid_at?: string | null;
  term_days_override?: number | null;
  invoice_url?: string;
  discount?: string;
  discount_note?: string | null;
  status?: string;
};

const props = defineProps<{
  expert: AdminExpertDetail;
}>();

const managementFilter = ref<AdminSubscriptionManagement>('all');

const {
  subscriptions,
  activeSubscription,
  isLoadingSubscriptions,
  plans,
  defaultTermDays,
  isLoadingPlans,
  registerInvoice,
  isRegistering,
  updateInvoice,
  isUpdatingInvoice,
  deleteInvoice,
  isDeletingInvoice,
  cancelSubscription,
  isCancellingSubscription,
} = useExpertSubscription({
  expertId: computed(() => props.expert.id),
  management: managementFilter,
});

const selectedSubscriptionId = ref<number | null>(null);
const showInvoiceForm = ref(false);
const editingInvoice = ref<AdminExpertInvoiceRecord | null>(null);
const showDeleteDialog = ref(false);
const invoicePendingDelete = ref<AdminExpertInvoiceRecord | null>(null);
const showCancelDialog = ref(false);

watch(subscriptions, (items) => {
  if (items.length === 0) {
    selectedSubscriptionId.value = null;
    return;
  }

  const selectedStillExists = items.some((subscription) => subscription.id === selectedSubscriptionId.value);
  if (!selectedStillExists) {
    selectedSubscriptionId.value = activeSubscription.value?.id ?? items[0]?.id ?? null;
  }
}, { immediate: true });

const selectedSubscription = computed(() =>
  subscriptions.value.find((subscription) => subscription.id === selectedSubscriptionId.value) ?? null,
);

const selectedPlanDetails = computed(() => {
  if (!selectedSubscription.value) return null;
  return plans.value.find((p) => p.id === selectedSubscription.value!.v2_plan_id) ?? null;
});

const selectedProviderSubscription = computed(() => selectedSubscription.value?.provider_subscription ?? null);
const selectedIsManaged = computed(() => isManagedSubscription(selectedSubscription.value));
const selectedProviderPresentation = computed(() => {
  const provider = selectedProviderSubscription.value;
  if (!provider) {
    return selectedIsManaged.value
      ? {
          label: 'Provider unavailable',
          variant: 'neutral' as const,
          detail: 'This record is Stripe-managed, but provider details are unavailable. Local changes remain disabled.',
        }
      : null;
  }
  return getProviderStatusPresentation(provider.status, provider.cancel_at_period_end);
});
const selectedBillingDisplay = computed(() => {
  const option = selectedProviderSubscription.value?.billing_option;
  if (option) {
    return {
      amountCents: option.amount_cents,
      currency: option.currency,
      recurrence: describeRecurrence(option.interval_unit, option.interval_count),
      label: option.label,
    };
  }

  const plan = selectedPlanDetails.value;
  if (!plan) return null;
  return {
    amountCents: plan.price_cents,
    currency: plan.currency,
    recurrence: `/${formatBillingCycle(plan.billing_cycle)}`,
    label: null,
  };
});
const linkedInvitationId = computed(() => getLinkedInvitationId(selectedSubscription.value));
const selectedAccentTone = computed(() => selectedSubscription.value
  ? getSubscriptionAccentTone(selectedSubscription.value, selectedSubscription.value.displayState.tone)
  : 'inactive');

const {
  invoices,
  isLoading: isLoadingInvoices,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
} = useExpertInvoices({
  expertId: computed(() => props.expert.id),
  subscriptionId: computed(() => selectedSubscriptionId.value ?? 0),
});

const invoiceFormMode = computed<'create' | 'edit'>(() => editingInvoice.value ? 'edit' : 'create');
const isSubmittingInvoiceForm = computed(() => isRegistering.value || isUpdatingInvoice.value);

function dollarsToCents(value: string) {
  return Math.round(Number(value) * 100);
}

async function handleRegisterInvoice(data: InvoiceFormSubmitData) {
  if (editingInvoice.value) {
    if (!canMutateInvoiceLocally(editingInvoice.value)) return;
    const payload: UpdateExpertSubscriptionInvoiceRequest = {
      amount_cents: dollarsToCents(data.amount),
      currency: data.currency || DEFAULT_CURRENCY,
      issued_at: data.issued_at,
      paid_at: data.paid_at === null ? null : data.paid_at || null,
      ...(data.term_days_override !== undefined ? { term_days_override: data.term_days_override } : {}),
      ...(data.invoice_url ? { invoice_url: data.invoice_url } : {}),
      ...(data.discount ? { discount_cents: dollarsToCents(data.discount) } : {}),
      ...(data.discount_note !== undefined ? { discount_note: data.discount_note } : {}),
      ...(data.status ? { status: data.status } : {}),
    };

    await updateInvoice.mutateAsync({
      subscriptionId: editingInvoice.value.subscription_id,
      invoiceId: editingInvoice.value.id,
      body: payload,
    });
    showInvoiceForm.value = false;
    editingInvoice.value = null;
    return;
  }

  const payload: RegisterExpertSubscriptionInvoiceRequest = {
    v2_plan_id: data.v2_plan_id,
    invoice_type: data.invoice_type,
    amount_cents: dollarsToCents(data.amount),
    currency: data.currency || DEFAULT_CURRENCY,
    issued_at: data.issued_at,
    paid_at: data.paid_at || '',
    ...(data.term_days_override !== undefined && data.term_days_override !== null
      ? { term_days_override: data.term_days_override }
      : {}),
    ...(data.invoice_url ? { invoice_url: data.invoice_url } : {}),
    ...(data.discount ? { discount_cents: dollarsToCents(data.discount) } : {}),
    ...(data.discount_note ? { discount_note: data.discount_note } : {}),
  };

  await registerInvoice.mutateAsync(payload);
  showInvoiceForm.value = false;
}

function formatCents(cents: number, curr?: string) {
  const currency = curr || DEFAULT_CURRENCY;

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

function formatDate(dateStr: string | null | undefined, timeZone?: string) {
  if (!dateStr) return '—';

  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  });
}

function formatBillingDate(dateStr: string | null | undefined) {
  return formatDate(dateStr, 'UTC');
}

function formatDateTime(dateStr: string | null | undefined) {
  if (!dateStr) return '—';

  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function invoiceStatusVariant(status: string) {
  const map: Record<string, 'success' | 'warn' | 'default' | 'highlight'> = {
    paid: 'success',
    issued: 'warn',
    draft: 'default',
    void: 'highlight',
    past_due: 'highlight',
  };
  return map[status] ?? 'default';
}

function getAccentDotColor(tone: string, isSelected: boolean) {
  const colors: Record<string, string> = {
    active: 'bg-emerald-400',
    ending: 'bg-amber-400',
    expired: 'bg-rose-400',
    inactive: 'bg-gray-400',
  };

  if (!isSelected) {
    colors.active = 'bg-emerald-500';
    colors.ending = 'bg-amber-500';
    colors.expired = 'bg-rose-500';
  }

  return colors[tone] ?? 'bg-gray-400';
}

function getAccentBorderColor(tone: string) {
  return {
    active: 'border-t-emerald-500',
    ending: 'border-t-amber-400',
    expired: 'border-t-rose-500',
    inactive: 'border-t-gray-300',
  }[tone] ?? 'border-t-gray-300';
}

function formatBillingCycle(cycle: string) {
  return { monthly: 'mo', yearly: 'yr', quarterly: 'qtr', annual: 'yr' }[cycle.toLowerCase()] ?? cycle;
}

function canCancelSubscription(sub: ExpertSubscriptionWithDisplay) {
  return canCancelLocally(sub)
    && (sub.displayState.tone === 'active' || sub.displayState.tone === 'ending');
}

function getNetAmountCents(invoice: AdminExpertInvoiceRecord) {
  return invoice.amount_cents - (invoice.discount_cents ?? 0);
}

function getInvoiceHref(invoice: AdminExpertInvoiceRecord) {
  return sanitizeHttpUrl(invoice.invoice_url);
}

function getRecentDiscountCount(subscriptionId: number) {
  const subscription = subscriptions.value.find((item) => item.id === subscriptionId);
  return subscription?.recent_invoices?.filter((invoice) => (invoice.discount_cents ?? 0) > 0).length ?? 0;
}

function getDefaultTermDays() {
  return defaultTermDays.value;
}

function openCreateInvoiceForm() {
  editingInvoice.value = null;
  showInvoiceForm.value = true;
}

function openEditInvoiceForm(invoice: AdminExpertInvoiceRecord) {
  if (!canMutateInvoiceLocally(invoice)) return;
  editingInvoice.value = invoice;
  showInvoiceForm.value = true;
}

function promptDeleteInvoice(invoice: AdminExpertInvoiceRecord) {
  if (!canMutateInvoiceLocally(invoice)) return;
  invoicePendingDelete.value = invoice;
  showDeleteDialog.value = true;
}

async function handleDeleteInvoice() {
  if (!invoicePendingDelete.value) return;

  await deleteInvoice.mutateAsync({
    subscriptionId: invoicePendingDelete.value.subscription_id,
    invoiceId: invoicePendingDelete.value.id,
  });
  showDeleteDialog.value = false;
  invoicePendingDelete.value = null;
}

async function handleCancelSubscription() {
  if (!selectedSubscription.value || !canCancelLocally(selectedSubscription.value)) return;

  await cancelSubscription.mutateAsync({
    subscriptionId: selectedSubscription.value.id,
  });
  showCancelDialog.value = false;
}

watch(showInvoiceForm, (isOpen) => {
  if (!isOpen) editingInvoice.value = null;
});

watch(showDeleteDialog, (isOpen) => {
  if (!isOpen) invoicePendingDelete.value = null;
});
</script>

<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-start justify-between gap-4">
      <div>
        <UiText as="h3" variant="h4" class="font-medium text-gray-900">Billing & Invoices</UiText>
        <UiText as="p" variant="p-small" class="mt-0.5 text-gray-400">
          Inspect Stripe-managed and manual billing without changing provider-owned records locally.
        </UiText>
      </div>
      <UiButton
        variant="primary"
        size="md"
        icon="plus-square"
        label="Register external invoice"
        :disabled="isLoadingPlans"
        @click="openCreateInvoiceForm"
      />
    </div>

    <ExpertPaymentInvitations :expert="expert" />

    <section class="flex flex-col gap-3" aria-labelledby="billing-chains-heading">
      <div class="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <UiText id="billing-chains-heading" as="h4" variant="h5" class="font-semibold text-gray-900">
            Subscription records
          </UiText>
          <UiText as="p" variant="caption-normal" class="mt-0.5 text-gray-500">
            Filter this expert's local records by the billing system that owns their lifecycle.
          </UiText>
        </div>
        <UiSegmentedControl
          v-model="managementFilter"
          name="subscription-management-filter"
          :options="adminSubscriptionManagementOptions"
          class="w-full md:w-[28rem]"
        />
      </div>
    </section>

    <!-- Loading -->
    <div v-if="isLoadingSubscriptions" class="flex items-center justify-center gap-2 py-16 text-gray-400">
      <div class="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-transparent" />
      <UiText as="span" variant="p-small">Loading subscriptions…</UiText>
    </div>

    <!-- Empty -->
    <div v-else-if="subscriptions.length === 0" class="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 py-14 text-center">
      <div class="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
        <UiIcon name="dollar-circle" class="h-6 w-6 text-gray-400" />
      </div>
      <UiText as="p" variant="p" class="font-medium text-gray-500">
        No {{ managementFilter === 'all' ? '' : managementFilter }} subscriptions found
      </UiText>
      <UiText as="p" variant="p-small" class="mt-1 text-gray-400">
        {{ managementFilter === 'managed'
          ? 'A Stripe-managed record appears after Checkout and webhook projection complete.'
          : managementFilter === 'manual'
            ? 'Register an external invoice to create a manual subscription record.'
            : 'Register an external invoice or issue a payment invitation to create one.' }}
      </UiText>
    </div>

    <!-- Content -->
    <template v-else>
      <!-- Subscription selector tabs -->
      <div class="flex flex-wrap gap-2">
        <button
          v-for="sub in subscriptions"
          :key="sub.id"
          class="flex items-center gap-2 rounded-lg border px-3.5 py-2 text-left transition-all duration-150"
          :class="selectedSubscriptionId === sub.id
            ? 'border-gray-900 bg-gray-900 shadow-sm'
            : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
          "
          @click="selectedSubscriptionId = sub.id"
        >
          <span
            class="h-2 w-2 shrink-0 rounded-full"
            :class="getAccentDotColor(
              getSubscriptionAccentTone(sub, sub.displayState.tone),
              selectedSubscriptionId === sub.id,
            )"
          />
          <span
            class="text-sm font-medium"
            :class="selectedSubscriptionId === sub.id ? 'text-white' : 'text-gray-800'"
          >
            {{ sub.plan.name }}
          </span>
          <span
            class="rounded-full px-1.5 py-px text-[10px] font-semibold uppercase tracking-wider"
            :class="selectedSubscriptionId === sub.id
              ? 'bg-white/15 text-white/70'
              : 'bg-gray-100 text-gray-500'
            "
          >
            {{ sub.is_managed && sub.provider_subscription
              ? getProviderStatusPresentation(
                  sub.provider_subscription.status,
                  sub.provider_subscription.cancel_at_period_end,
                ).label
              : sub.displayState.label }}
          </span>
          <span
            class="text-[10px] font-semibold uppercase tracking-wider"
            :class="selectedSubscriptionId === sub.id ? 'text-white/60' : 'text-gray-400'"
          >
            {{ sub.is_managed ? 'Stripe' : 'Manual' }}
          </span>
          <span
            class="ml-0.5 text-[11px] tabular-nums"
            :class="selectedSubscriptionId === sub.id ? 'text-white/40' : 'text-gray-400'"
          >
            {{ sub.invoices_count }}
          </span>
        </button>
      </div>

      <!-- Selected subscription — billing card -->
      <div
        v-if="selectedSubscription"
        class="overflow-hidden rounded-xl border border-gray-200 border-t-2 bg-white"
        :class="getAccentBorderColor(selectedAccentTone)"
      >
        <div class="px-5 py-4">
          <!-- Top row: plan identity + status -->
          <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <UiText as="h4" variant="h4" class="font-semibold text-gray-900">
                {{ selectedSubscription.plan.name }}
              </UiText>
              <div v-if="selectedBillingDisplay" class="mt-1 flex flex-wrap items-baseline gap-x-1.5 gap-y-1">
                <span class="text-xl font-bold tabular-nums tracking-tight text-gray-900">
                  {{ formatCents(selectedBillingDisplay.amountCents, selectedBillingDisplay.currency) }}
                </span>
                <span class="text-sm text-gray-400">
                  {{ selectedBillingDisplay.recurrence }}
                </span>
                <UiChip
                  v-if="selectedBillingDisplay.label"
                  :text="selectedBillingDisplay.label"
                  variant="outline"
                  size="xs"
                />
              </div>
            </div>

            <div class="flex flex-col items-start gap-1.5 sm:items-end">
              <div class="flex flex-wrap items-center gap-1.5 sm:justify-end">
                <UiChip
                  :text="selectedIsManaged ? 'Stripe-managed' : 'Manual'"
                  :variant="selectedIsManaged ? 'outline' : 'neutral'"
                  size="xs"
                  rounded="full"
                />
                <UiChip
                  v-if="selectedProviderPresentation"
                  :text="selectedProviderPresentation.label"
                  :variant="selectedProviderPresentation.variant"
                  size="xs"
                  rounded="full"
                />
              </div>
              <UiChip
                v-if="!selectedIsManaged"
                :text="selectedSubscription.displayState.label"
                :variant="selectedSubscription.displayState.variant"
                size="xs"
                rounded="full"
              />
              <UiText as="span" variant="caption-normal" class="text-gray-500">
                {{ selectedProviderPresentation?.detail ?? selectedSubscription.displayState.detail }}
              </UiText>
            </div>
          </div>

          <div
            v-if="selectedProviderSubscription"
            class="mt-4 grid gap-4 rounded-lg bg-gray-50 px-4 py-3 sm:grid-cols-2 xl:grid-cols-4"
          >
            <div>
              <UiText as="p" variant="caption-normal" class="text-gray-400">Provider record</UiText>
              <div class="mt-0.5 flex flex-wrap items-center gap-1.5">
                <UiText as="p" variant="p-small" class="font-medium text-gray-800">
                  {{ selectedProviderSubscription.provider === 'stripe' ? 'Stripe' : selectedProviderSubscription.provider }}
                </UiText>
                <UiChip
                  v-if="selectedProviderSubscription.mode === 'test'"
                  text="Test mode"
                  variant="warn"
                  size="xs"
                  rounded="full"
                />
              </div>
              <UiText as="p" variant="caption-normal" class="mt-0.5 break-all font-mono text-gray-500">
                {{ selectedProviderSubscription.provider_subscription_id }}
              </UiText>
            </div>
            <div>
              <UiText as="p" variant="caption-normal" class="text-gray-400">Provider period (UTC)</UiText>
              <UiText as="p" variant="p-small" class="mt-0.5 font-medium text-gray-800">
                {{ formatBillingDate(selectedProviderSubscription.current_period_starts_at) }}
                – {{ formatBillingDate(selectedProviderSubscription.current_period_ends_at) }}
              </UiText>
            </div>
            <div>
              <UiText as="p" variant="caption-normal" class="text-gray-400">Payment invitation</UiText>
              <UiText as="p" variant="p-small" class="mt-0.5 font-medium text-gray-800">
                {{ linkedInvitationId ? `Invitation #${linkedInvitationId}` : 'Not available' }}
              </UiText>
              <UiText
                v-if="selectedProviderSubscription.invitation?.status"
                as="p"
                variant="caption-normal"
                class="mt-0.5 capitalize text-gray-500"
              >
                {{ selectedProviderSubscription.invitation.status }}
              </UiText>
            </div>
            <div>
              <UiText as="p" variant="caption-normal" class="text-gray-400">Last provider update</UiText>
              <UiText as="p" variant="p-small" class="mt-0.5 font-medium text-gray-800">
                {{ formatDateTime(selectedProviderSubscription.provider_state_updated_at) }}
              </UiText>
            </div>
          </div>

          <UiInfoBox
            v-if="selectedIsManaged"
            class="mt-4"
            variant="info"
            title="Stripe owns this subscription lifecycle"
          >
            <UiText as="p" variant="caption-normal">
              Cancel, payment-method, and renewal changes belong in Stripe and return through signed webhooks.
              External invoice registration remains available as an independent admin fallback and does not stop Stripe billing.
            </UiText>
          </UiInfoBox>

          <!-- Separator -->
          <div class="my-3.5 border-t border-gray-100" />

          <!-- Bottom row: dates + meta + cancel -->
          <div class="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
            <div class="flex flex-wrap items-center gap-x-4 gap-y-1.5">
              <span class="text-xs text-gray-500">
                <span class="text-gray-400">Started</span> {{ formatDate(selectedSubscription.starts_at) }}
              </span>
              <span v-if="selectedSubscription.renews_at" class="text-xs text-gray-500">
                <span class="text-gray-400">Renews</span> {{ formatDate(selectedSubscription.renews_at) }}
              </span>
              <span v-if="selectedSubscription.ends_at" class="text-xs text-gray-500">
                <span class="text-gray-400">Expires</span> {{ formatDate(selectedSubscription.ends_at) }}
              </span>

              <div class="h-3 w-px bg-gray-200" />

              <UiText as="span" variant="caption-normal" class="tabular-nums text-gray-500">
                {{ selectedSubscription.invoices_count }} invoice{{ selectedSubscription.invoices_count !== 1 ? 's' : '' }}
              </UiText>
              <UiChip
                v-if="getRecentDiscountCount(selectedSubscription.id) > 0"
                :text="`${getRecentDiscountCount(selectedSubscription.id)} discounted`"
                variant="highlight"
                size="xs"
                rounded="full"
              />
            </div>

            <UiButton
              v-if="canCancelSubscription(selectedSubscription)"
              class="self-start"
              variant="danger"
              size="sm"
              label="Cancel manual subscription"
              @click="showCancelDialog = true"
            />
          </div>
        </div>
      </div>

      <!-- Invoice ledger -->
      <div v-if="selectedSubscriptionId">
        <div class="mb-3">
          <UiText as="h4" variant="h6" class="font-semibold text-gray-900">Invoice ledger</UiText>
          <UiText as="p" variant="caption-normal" class="mt-0.5 text-gray-500">
            Stripe invoices are read-only locally; external invoices retain the existing admin controls.
          </UiText>
        </div>

        <!-- Loading invoices -->
        <div v-if="isLoadingInvoices" class="flex items-center justify-center gap-2 py-10 text-gray-400">
          <div class="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-transparent" />
          <UiText as="span" variant="p-small">Loading invoices…</UiText>
        </div>

        <!-- Empty invoices -->
        <div v-else-if="invoices.length === 0" class="rounded-lg border border-dashed border-gray-200 py-10 text-center">
          <UiText as="p" variant="p-small" class="text-gray-400">No invoices for this subscription</UiText>
        </div>

        <!-- Invoice rows -->
        <div v-else class="space-y-1.5">
          <div
            v-for="invoice in invoices"
            :key="invoice.id"
            class="group overflow-hidden rounded-xl border border-gray-100 bg-white transition-colors hover:border-gray-200"
          >
            <!-- Main row -->
            <div class="flex flex-col gap-3 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
              <!-- Identity cluster -->
              <div class="flex flex-wrap items-center gap-2">
                <UiChip
                  :text="invoice.status"
                  :variant="invoiceStatusVariant(invoice.status)"
                  size="xs"
                  rounded="full"
                />
                <UiText as="span" variant="p-small" class="capitalize text-gray-600">
                  {{ isManagedInvoice(invoice) ? 'Stripe invoice' : 'External invoice' }}
                </UiText>
                <span class="font-mono text-[11px] text-gray-400">#{{ invoice.id }}</span>
                <UiChip
                  v-if="(invoice.discount_cents ?? 0) > 0"
                  text="Discounted"
                  variant="highlight"
                  size="xs"
                  rounded="full"
                />
              </div>

              <!-- Dates -->
              <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                <span class="text-gray-500">
                  <span class="text-gray-400">Issued</span> {{ formatDate(invoice.issued_at) }}
                </span>
                <span class="text-gray-500">
                  <span class="text-gray-400">Paid</span> {{ formatDate(invoice.paid_at) }}
                </span>
                <span v-if="!isManagedInvoice(invoice)" class="text-gray-500">
                  <span class="text-gray-400">Term</span>
                  <template v-if="invoice.term_days_override != null">
                    {{ invoice.term_days_override }}d
                  </template>
                  <template v-else-if="getDefaultTermDays() != null">
                    {{ getDefaultTermDays() }}d default
                  </template>
                  <template v-else>
                    default
                  </template>
                </span>
                <span class="text-gray-500">
                  <span class="text-gray-400">Updated</span> {{ formatDateTime(invoice.updated_at) }}
                </span>
                <span v-if="isManagedInvoice(invoice)" class="text-gray-500">
                  <span class="text-gray-400">Service period (UTC)</span>
                  {{ formatBillingDate(invoice.service_period_starts_at) }}–{{ formatBillingDate(invoice.service_period_ends_at) }}
                </span>
              </div>

              <!-- Amounts + actions -->
              <div class="flex items-center gap-3">
                <div class="flex items-center gap-2 tabular-nums">
                  <template v-if="(invoice.discount_cents ?? 0) > 0">
                    <span class="text-xs text-gray-400 line-through">
                      {{ formatCents(invoice.amount_cents, invoice.currency) }}
                    </span>
                    <span class="text-[11px] font-medium text-rose-500">
                      −{{ formatCents(invoice.discount_cents ?? 0, invoice.currency) }}
                    </span>
                  </template>
                  <span class="text-sm font-semibold text-gray-900">
                    {{ formatCents(getNetAmountCents(invoice), invoice.currency) }}
                  </span>
                </div>

                <div class="h-5 w-px bg-gray-100" />

                <div class="flex items-center gap-1.5">
                  <UiTooltip v-if="getInvoiceHref(invoice)" content="Open invoice">
                    <UiIconButton
                      icon="link-external"
                      variant="outline"
                      class="size-9 rounded-md"
                      :href="getInvoiceHref(invoice) ?? undefined"
                      target="_blank"
                      rel="noopener noreferrer"
                      :aria-label="`Open invoice #${invoice.id}`"
                      @click.stop
                    />
                  </UiTooltip>
                  <UiTooltip v-if="canMutateInvoiceLocally(invoice)" content="Edit external invoice">
                    <UiIconButton
                      icon="pencil"
                      variant="outline"
                      class="size-9 rounded-md"
                      :aria-label="`Edit invoice #${invoice.id}`"
                      @click="openEditInvoiceForm(invoice)"
                    />
                  </UiTooltip>
                  <UiTooltip v-if="canMutateInvoiceLocally(invoice)" content="Delete external invoice">
                    <UiIconButton
                      icon="delete"
                      variant="danger"
                      class="size-9 rounded-md"
                      :aria-label="`Delete invoice #${invoice.id}`"
                      @click="promptDeleteInvoice(invoice)"
                    />
                  </UiTooltip>
                  <UiChip
                    v-if="isManagedInvoice(invoice)"
                    text="Read-only"
                    variant="outline"
                    size="xs"
                    tooltip="Managed invoice history is immutable locally"
                  />
                </div>
              </div>
            </div>

            <!-- Discount note (conditional) -->
            <div v-if="invoice.discount_note" class="border-t border-gray-50 bg-rose-50/40 px-4 py-2">
              <UiText as="p" variant="caption-normal" class="text-rose-600">
                {{ invoice.discount_note }}
              </UiText>
            </div>
          </div>

          <!-- Load more -->
          <div v-if="hasNextPage" class="pt-3 text-center">
            <UiButton
              variant="outline"
              size="md"
              label="Load More"
              :loading="isFetchingNextPage"
              @click="fetchNextPage"
            />
          </div>
        </div>
      </div>
    </template>

    <!-- Invoice form aside -->
    <InvoiceForm
      v-model="showInvoiceForm"
      :plans="plans"
      :default-term-days="defaultTermDays"
      :invoice="editingInvoice"
      :mode="invoiceFormMode"
      :is-submitting="isSubmittingInvoiceForm"
      @submit="handleRegisterInvoice"
    />

    <!-- Delete invoice confirmation -->
    <UiDialog
      v-model="showDeleteDialog"
      v-model:loading="isDeletingInvoice"
      title="Delete Invoice"
      primaryActionLabel="Delete Invoice"
      secondaryActionLabel="Cancel"
      size="sm"
      @primaryAction="handleDeleteInvoice"
      @secondaryAction="showDeleteDialog = false"
      @close="showDeleteDialog = false"
    >
      <UiText as="p" variant="p-small" class="text-gray-600">
        Delete invoice #{{ invoicePendingDelete?.id }} for {{ selectedSubscription?.plan.name || 'this subscription' }}? This cannot be undone.
      </UiText>
      <div v-if="invoicePendingDelete" class="mt-3 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
        <UiText as="span" variant="caption-normal" class="tabular-nums text-gray-500">
          Gross {{ formatCents(invoicePendingDelete.amount_cents, invoicePendingDelete.currency) }} ·
          Net {{ formatCents(getNetAmountCents(invoicePendingDelete), invoicePendingDelete.currency) }}
        </UiText>
      </div>
    </UiDialog>

    <!-- Cancel subscription confirmation -->
    <UiDialog
      v-model="showCancelDialog"
      v-model:loading="isCancellingSubscription"
      title="Cancel Subscription"
      primaryActionLabel="Cancel Subscription"
      secondaryActionLabel="Keep Subscription"
      size="sm"
      @primaryAction="handleCancelSubscription"
      @secondaryAction="showCancelDialog = false"
      @close="showCancelDialog = false"
    >
      <UiText as="p" variant="p-small" class="text-gray-600">
        Cancel the <span class="font-medium text-gray-800">{{ selectedSubscription?.plan.name }}</span> subscription? This will stop future renewals.
      </UiText>
      <div v-if="selectedSubscription?.renews_at" class="mt-3 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2">
        <UiText as="span" variant="caption-normal" class="text-amber-700">
          Next renewal was scheduled for {{ formatDate(selectedSubscription.renews_at) }}
        </UiText>
      </div>
      <div v-if="selectedPlanDetails" class="mt-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
        <UiText as="span" variant="caption-normal" class="tabular-nums text-gray-500">
          {{ formatCents(selectedPlanDetails.price_cents, selectedPlanDetails.currency) }}/{{ formatBillingCycle(selectedPlanDetails.billing_cycle) }} · {{ selectedSubscription?.invoices_count }} invoice{{ selectedSubscription?.invoices_count !== 1 ? 's' : '' }} on record
        </UiText>
      </div>
    </UiDialog>
  </div>
</template>
